import { UserApiService } from '../services/access-management';
import { UserManagementApiService } from '../services/access-management';
import { RoleManagementApiService } from '../services/access-management';
import { PermissionManagementApiService } from '../services/access-management';
import { OrganizationManagementApiService } from '../services/access-management';
import { getCurrentOrg } from './getCurrentOrg';
import type { UserRequest } from '../services/access-management';
import type { PageItemUserDto } from '../services/access-management';
import type { OrganizationDto } from '../services/access-management';
import type { AssignRoleRequest } from '../services/access-management';
import type { RoleDto, RoleRequest } from '../services/access-management';
import type { PermissionCreateRequest } from '../services/access-management';
import type { OrganizationCreateRequest } from '../services/access-management';

export const updatePermissions = (roleId: string, permissionIds: string[]) => {
  return RoleManagementApiService.updatePermissions(roleId, { permissionIds });
};
export const getPermissionsList = () => {
  return PermissionManagementApiService.getAllPermissions();
};
export const addPermission = (permission: PermissionCreateRequest) => {
  return PermissionManagementApiService.createPermission(permission);
};
export const updatePermission = (
  permissionId: string,
  permission: PermissionCreateRequest
) => {
  return PermissionManagementApiService.updatePermissionDescription(
    permissionId,
    permission
  );
};
export const deletePermission = (permissionId: string, force = true) => {
  return PermissionManagementApiService.deletePermission(permissionId, force);
};

export const getRoles = (): Promise<Array<RoleDto>> => {
  return RoleManagementApiService.getAllRoles();
};
export const addRole = (values: RoleRequest) => {
  return RoleManagementApiService.createRole(values);
};
export const updateRole = (roleId: string, values: RoleRequest) => {
  return RoleManagementApiService.updateRole(roleId, values);
};
export const duplicateRole = (id: string) => {
  return RoleManagementApiService.duplicateRole(id);
};
export const deleteRole = (id: string) => {
  return RoleManagementApiService.deleteRole(id);
};
export const removePermissionFromRole = (
  roleId: string,
  permissionId: string
) => {
  return RoleManagementApiService.removePermissionFromRole(
    roleId,
    permissionId
  );
};

export const getUserOrgs = async () => {
  return UserApiService.getAffiliatedOrganizations();
};

export const getAllUsers = async (
  page?: number,
  pageSize?: number,
  sort?: SortState<string>
) => {
  return UserManagementApiService.getUsers(
    undefined,
    page,
    pageSize,
    sort?.name ? [`${sort.name},${sort.direction}`] : []
  );
};
export const getOrgUsers = async (
  organizationId: string,
  page?: number,
  pageSize?: number,
  sort?: SortState<string>
) => {
  return UserManagementApiService.getUsers(
    organizationId,
    page,
    pageSize,
    sort?.name ? [`${sort.name},${sort.direction}`] : []
  );
};
export const addUser = async (request: UserRequest) => {
  return UserManagementApiService.createUser(request);
};
export const inviteUser = async (userId: string) => {
  return UserManagementApiService.inviteUser(userId);
};
export const assignUserToRole = (payload: AssignUserToRolePayload) => {
  return OrganizationManagementApiService.assignUserRole(
    payload.organizationId,
    { userId: payload.userId, roleId: payload.roleId }
  );
};
export const unassignUserFromOrg = (payload: UnassignUserFromOrgPayload) => {
  return OrganizationManagementApiService.unassignUserFromOrganization(
    payload.organizationId,
    payload.userId
  );
};
export const unassignUserFromRole = (payload: UnassignUserFromRolePayload) => {
  return OrganizationManagementApiService.unassignUserRole(
    payload.organizationId,
    payload
  );
};
export const addUserToOrganization = (
  organizationId: string,
  userId: string
) => {
  return OrganizationManagementApiService.addUserToOrganization(
    organizationId,
    userId
  );
};

export const getOrganizations = () => {
  return OrganizationManagementApiService.getAllOrganizations();
};
export const deleteOrganization = (id: string) => {
  return OrganizationManagementApiService.deleteOrganization(id);
};
export const addOrganization = (value: OrganizationCreatePayload) => {
  return OrganizationManagementApiService.createOrganization(value);
};
export const editOrganization = (
  id: string,
  value: OrganizationCreatePayload
) => {
  return OrganizationManagementApiService.updateOrganization(id, value);
};
export const getOrganization = (id: string) => {
  return OrganizationManagementApiService.getOrganizationById(id);
};
export const getApiKey = (id: string) => {
  return OrganizationManagementApiService.getApiKey(getCurrentOrg(), id);
};

export type { RoleDto, RoleRequest, PermissionCreateRequest };
export type { PermissionDto, UserOrganizationDto } from '../services';
export type UserFormType = UserRequest;
export type UserListType = PageItemUserDto;
export type OrganizationType = OrganizationDto;
export type OrganizationCreatePayload = OrganizationCreateRequest;
export type AssignUserToRolePayload = AssignRoleRequest & {
  organizationId: string;
};
export type UnassignUserFromOrgPayload = {
  userId: string;
  organizationId: string;
};
export type UnassignUserFromRolePayload = {
  userId: string;
  roleId: string;
  organizationId: string;
};
